import Decimal from 'decimal.js/decimal'
import { dateConstants } from '@/modules/base/util/constants'
import { PriceValues } from '@/modules/moduleSubscription/types/module'
import checkDiscount from '@/modules/base/components/modal/upgrade-modal/subscription-selection/_helpers/checkDiscount'

export const MONTHS_PER_YEAR = 12

const calculatePrice = (priceValues: PriceValues, type?: string, period?: string): Decimal => {
  if (!priceValues) return null
  const { pricePerDuration, reducedPrice } = priceValues
  let price = new Decimal(pricePerDuration)

  if (reducedPrice) {
    if (type) {
      const applyDiscount = checkDiscount(type, reducedPrice)
      if (!applyDiscount) {
        return price
      }
    }

    const { percent: reducedPricePercent, duration: reducedPriceDuration = 12, period: reducedPricePeriod } = reducedPrice
    const reductionFactor = new Decimal(100).minus(new Decimal(reducedPricePercent)).dividedBy(100)

    if (period === dateConstants.MONTH) {
      // monthly billing: just apply the discount percentage directly
      price = price.times(reductionFactor)
    } else if (reducedPricePeriod === dateConstants.YEAR && reducedPriceDuration >= 1) {
      // yearly billing with a full year+ discount: apply simple percentage
      price = price.times(reductionFactor)
    } else {
      // yearly billing with a partial discount (in months)
      const configuredDiscountMonths = (reducedPricePeriod === dateConstants.MONTH || reducedPricePeriod === dateConstants.FOREVER)
        ? reducedPriceDuration
        : reducedPriceDuration * MONTHS_PER_YEAR
      // only the billed year is priced here, so a longer discount simply covers all of its months.
      // Without the cap the remainder below turns negative and the price collapses.
      const discountMonths = Math.min(configuredDiscountMonths, MONTHS_PER_YEAR)
      const pricePerMonth = price.dividedBy(MONTHS_PER_YEAR)

      if (discountMonths >= MONTHS_PER_YEAR) {
        // every month is discounted, so the percentage applies to the interval price as a whole
        price = price.times(reductionFactor)
      } else {
        // Only part of the year is discounted and both stretches are listed with their own monthly
        // price, so the year has to be summed up from exactly those rounded amounts - see
        // getDiscountSplit. Blending the unrounded fractions instead leaves the total a few cents
        // below the sum of the listed rows and below the confirmation email, which does the same.
        price = pricePerMonth.times(reductionFactor).toDP(2).times(discountMonths)
          .plus(pricePerMonth.toDP(2).times(MONTHS_PER_YEAR - discountMonths))
      }
    }
  }
  return price
}

export default calculatePrice
